'use client'

import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useSession } from 'next-auth/react'
import { io, Socket } from 'socket.io-client'
import type {
  HelpChatConversation,
  HelpChatMessage,
  HelpAdminAvailability
} from '@/types/helpChat'

type ConnectionStatus = 'connecting' | 'connected' | 'failed'

interface HelpChatSocketContextType {
  socket: Socket | null
  isConnected: boolean
  connectionStatus: ConnectionStatus
  currentConversation: HelpChatConversation | null
  messages: HelpChatMessage[]
  isLoading: boolean
  error: string | null
  startConversation: (type?: 'ai' | 'admin') => Promise<void>
  sendMessage: (message: string, file?: string | null) => Promise<void>
  uploadFile: (file: File) => Promise<string | null>
  transferToAdmin: () => Promise<void>
  cancelTransfer: () => Promise<void>
  startNewConversation: () => Promise<void>
  closeConversation: (rating?: number, feedback?: string) => Promise<void>
  checkAdminAvailability: () => Promise<HelpAdminAvailability>
  executeExampleProductAction: () => Promise<void>
  setTyping: (isTyping: boolean) => void
  isOtherTyping: boolean
  isChatExpanded: boolean
  setIsChatExpanded: (expanded: boolean) => void
  isChatOpen: boolean
  setIsChatOpen: (open: boolean) => void
}

const HelpChatSocketContext = createContext<HelpChatSocketContextType | undefined>(undefined)

type HelpCenterChat = {
  id: string
  message: string | null
  file: string | null
  roomId: string
  type: 'message' | 'alert' | 'system'
  sentByUser: boolean
  sentByAI: boolean
  sentAdminId: string | null
  sentAt: string
  button?: { title: string; url: string } | null
  sentAdmin?: { id: string; firstName: string | null } | null
}

type HelpCenterRoom = {
  id: string
  status: 'ai' | 'pending' | 'active' | 'completed' | 'abandoned'
  vendorId: string | null
  userId: string | null
  adminId: string | null
  name: string
  email: string
  phone: string
  createdAt: string
  endedAt: string | null
}

function chatToMessage(chat: HelpCenterChat, vendorId: string): HelpChatMessage {
  const metadata: any = {}
  if (chat.button) {
    metadata.pageRoute = chat.button.url
    metadata.buttonTitle = chat.button.title
  }
  if (chat.sentAdmin) {
    // Only first name is exposed to vendors — last name is operator PII.
    metadata.adminName = chat.sentAdmin.firstName || null
  }
  return {
    id: chat.id,
    conversationId: chat.roomId,
    vendorId,
    senderType: chat.sentByUser ? 'user' : chat.sentByAI ? 'ai' : 'admin',
    senderId: chat.sentAdminId || '',
    messageType: chat.type === 'alert' || chat.type === 'system' ? 'system' : 'text',
    message: chat.message || '',
    file: chat.file || null,
    metadata: Object.keys(metadata).length ? metadata : undefined,
    isRead: true,
    createdAt: chat.sentAt
  }
}

function roomToConversation(room: HelpCenterRoom): HelpChatConversation {
  const status = room.status === 'completed' || room.status === 'abandoned' ? 'closed' : 'active'
  const type: 'ai' | 'admin' = room.status === 'pending' || room.status === 'active' ? 'admin' : 'ai'
  return {
    id: room.id,
    vendorId: room.vendorId || '',
    userId: room.userId || '',
    status,
    type,
    adminId: room.adminId || undefined,
    createdAt: room.createdAt,
    updatedAt: room.createdAt
  }
}

export function HelpChatSocketProvider({ children }: { children: React.ReactNode }) {
  const { data: session } = useSession()
  const [socket, setSocket] = useState<Socket | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('connecting')
  const [currentConversation, setCurrentConversation] = useState<HelpChatConversation | null>(null)
  const [messages, setMessages] = useState<HelpChatMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isOtherTyping, setIsOtherTyping] = useState(false)

  // Persist chat state in sessionStorage. Initialize to false on both server
  // and client so the first client render matches SSR; hydrate the saved
  // value after mount to avoid hydration mismatches.
  const [isChatExpanded, setIsChatExpandedState] = useState(false)
  const [isChatOpen, setIsChatOpenState] = useState(false)

  useEffect(() => {
    const savedExpanded = sessionStorage.getItem('helpChatExpanded')
    if (savedExpanded) setIsChatExpandedState(JSON.parse(savedExpanded))
    const savedOpen = sessionStorage.getItem('helpChatOpen')
    if (savedOpen) setIsChatOpenState(JSON.parse(savedOpen))
  }, [])

  const setIsChatExpanded = (expanded: boolean) => {
    setIsChatExpandedState(expanded)
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('helpChatExpanded', JSON.stringify(expanded))
    }
  }

  const setIsChatOpen = (open: boolean) => {
    setIsChatOpenState(open)
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('helpChatOpen', JSON.stringify(open))
    }
  }

  const vendorId = session?.user?.vendor?.id
  const conversationRef = useRef<HelpChatConversation | null>(null)
  conversationRef.current = currentConversation
  const isSelfTypingRef = useRef(false)

  // Hydrate chat history via REST as soon as the user is authenticated. This
  // is independent of the socket — guarantees the user sees their existing
  // conversation immediately on a page refresh, without waiting for socket
  // round-trips or relying on per-socket replay timing.
  // `replace` swaps the whole stream (switching to another room); otherwise a socket-populated
  // stream is left alone so a stale snapshot never clobbers a just-replayed greeting.
  const loadMine = async ({ replace = false }: { replace?: boolean } = {}) => {
    if (!session?.user?.accessToken || !vendorId) return
    const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:3001'
    try {
      const res: any = await fetch(`${backendUrl}/helpCenter/room/mine`, {
        headers: {
          'Authorization': `Bearer ${session.user.accessToken}`,
          'Vendor': vendorId,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }).then((r) => r.json())
      const room: HelpCenterRoom | null = res?.data || null
      if (!room) return
      setCurrentConversation(roomToConversation(room))
      const chats: HelpCenterChat[] = (room as any).chats || []
      if (replace) setMessages(chats.map((c) => chatToMessage(c, vendorId)))
      else if (chats.length) setMessages(prev => prev.length ? prev : chats.map((c) => chatToMessage(c, vendorId)))
    } catch {}
  }
  const loadMineRef = useRef(loadMine)
  loadMineRef.current = loadMine
  useEffect(() => { loadMineRef.current() }, [session?.user?.accessToken, vendorId])

  useEffect(() => {
    if (!session?.user?.accessToken || !vendorId) return
    const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:3001'

    const socketInstance = io(backendUrl, {
      path: '/help-center/',
      auth: {
        token: session.user.accessToken,
        vendorId
      },
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    })

    socketInstance.on('connect', () => {
      setIsConnected(true)
      setConnectionStatus('connected')
      setError(null)
      // Do NOT emit start-chat here. Creating the room on every connect spawns
      // an empty AI room per login and floods the admin chat list. The room is
      // now created lazily when the vendor actually opens the bubble (see the
      // isChatOpen effect below). The backend still auto-joins any pre-existing
      // in-progress room on connect, so live messages for an open conversation
      // keep arriving without it.
    })

    socketInstance.on('connect_error', (err) => {
      setIsConnected(false)
      setConnectionStatus('failed')
      setError(err.message)
    })

    socketInstance.on('reconnect_attempt', () => {
      setConnectionStatus('connecting')
    })

    socketInstance.on('disconnect', () => {
      setIsConnected(false)
      // Disconnect (vs failed connect): treat as connecting since reconnection
      // is enabled — avoid showing the error state during transient drops.
      setConnectionStatus((prev) => prev === 'failed' ? prev : 'connecting')
    })

    socketInstance.on('receive-message', ({ roomId, chat }: { roomId: string; chat: HelpCenterChat }) => {
      // this socket can sit in several rooms (old + new chat); only the shown one renders
      const conv = conversationRef.current
      if (roomId && conv && roomId !== conv.id) return
      setMessages((prev) => {
        if (prev.some((m) => m.id === chat.id)) return prev
        return [...prev, chatToMessage(chat, vendorId)]
      })
    })

    socketInstance.on('room-update', ({ roomId, status, adminId }: { roomId: string; status: HelpCenterRoom['status']; adminId: string | null }) => {
      const conv = conversationRef.current
      if (roomId && conv && roomId !== conv.id) {
        // An operator continued one of our other (closed) chats. Clearing the stream
        // re-arms the open effect: start-chat is get-or-create, so it hands back that
        // open room, joins this socket to it and replays its history. A plain REST
        // reload would show the thread but never join the room for live messages.
        if (status === 'active') {
          setMessages([])
          setCurrentConversation(null)
          startRequestedRef.current = false
        }
        return
      }
      setCurrentConversation((prev) => {
        if (!prev) return prev
        const newConvStatus = status === 'completed' || status === 'abandoned' ? 'closed' : 'active'
        const newType: 'ai' | 'admin' = status === 'pending' || status === 'active' ? 'admin' : 'ai'
        return { ...prev, status: newConvStatus, type: newType, adminId: adminId || undefined }
      })
    })

    socketInstance.on('ai-start-typing', () => setIsOtherTyping(true))
    socketInstance.on('ai-stop-typing', () => setIsOtherTyping(false))
    // The helpCenter server broadcasts `typing` / `stop-typing` to the whole
    // room (including the originator). Ignore the echo of our own typing so
    // it doesn't disable our own input.
    socketInstance.on('typing', () => { if (!isSelfTypingRef.current) setIsOtherTyping(true) })
    socketInstance.on('stop-typing', () => { if (!isSelfTypingRef.current) setIsOtherTyping(false) })

    socketInstance.on('end-success', () => {
      setCurrentConversation((prev) => prev ? { ...prev, status: 'closed' } : null)
    })

    socketInstance.on('start-success', (room: HelpCenterRoom) => {
      setCurrentConversation(roomToConversation(room))
    })

    socketInstance.on('start-error', (err: string) => {
      setError(typeof err === 'string' ? err : 'Чат эхлүүлэхэд алдаа гарлаа')
      setIsLoading(false)
    })

    setSocket(socketInstance)

    return () => {
      socketInstance.disconnect()
    }
  }, [session?.user?.accessToken, vendorId])

  // When the vendor opens the bubble, ensure the room + history exist.
  // start-chat is get-or-create on the backend: it replays existing history
  // (the provider dedups by chat.id) or posts the AI greeting for a fresh room.
  // Opening the bubble only rejoins a room that already exists (replaying its history).
  // With no room the widget shows "Чат эхлүүлэх" and nothing is created until the vendor
  // presses it — opening and closing the bubble used to leave a greeting-only room behind.
  const startRequestedRef = useRef(false)
  useEffect(() => {
    if (!isChatOpen) { startRequestedRef.current = false; return }
    if (isConnected && socket && conversationRef.current && messages.length === 0 && !startRequestedRef.current) {
      startRequestedRef.current = true
      socket.emit('start-chat', {
        name: session?.user?.vendor?.name || session?.user?.name || '',
        email: session?.user?.email || '',
        phone: session?.user?.phone || ''
      })
    }
  }, [isChatOpen, isConnected, socket, messages.length, session])

  const startConversation = async (_type: 'ai' | 'admin' = 'ai') => {
    if (!socket || !session?.user) {
      setError('Socket not connected')
      return
    }
    setIsLoading(true)
    setError(null)
    socket.emit('start-chat', {
      name: session.user.vendor?.name || session.user.name || '',
      email: session.user.email || '',
      phone: session.user.phone || ''
    })
    setIsLoading(false)
  }

  const sendMessage = async (message: string, file?: string | null) => {
    const conv = conversationRef.current
    if (!socket || !conv) {
      setError('No active conversation')
      return
    }
    setError(null)
    socket.emit('send-message', { roomId: conv.id, message, file: file || undefined })
  }

  const uploadFile = async (file: File): Promise<string | null> => {
    const conv = conversationRef.current
    const token = session?.user?.accessToken
    if (!conv || !token || !vendorId) return null
    const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:3001'
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await fetch(`${backendUrl}/helpCenter/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Vendor': vendorId,
          'roomid': conv.id,
        },
        body: formData,
      })
      if (!res.ok) return null
      const json = await res.json()
      return json?.data?.[0] || null
    } catch {
      return null
    }
  }

  const transferToAdmin = async () => {
    const conv = conversationRef.current
    if (!socket || !conv) {
      setError('No active conversation')
      return
    }
    setError(null)
    socket.emit('send-message', { roomId: conv.id, message: 'оператортой холбогдъё' })
  }

  // Leave the operator queue; the server answers with room-update (status 'ai').
  const cancelTransfer = async () => {
    const conv = conversationRef.current
    if (!socket || !conv) return
    setError(null)
    socket.emit('cancel-transfer', { roomId: conv.id })
  }

  // Closed chat → fresh room: clear the stream and ask the server for a new room right away.
  const startNewConversation = async () => {
    setMessages([])
    setCurrentConversation(null)
    setError(null)
    await startConversation('ai')
  }

  const closeConversation = async (_rating?: number, _feedback?: string) => {
    const conv = conversationRef.current
    if (!socket || !conv) {
      setError('No active conversation')
      return
    }
    setError(null)
    socket.emit('end-chat', { roomId: conv.id })
    setCurrentConversation(null)
    setMessages([])
  }

  const checkAdminAvailability = async (): Promise<HelpAdminAvailability> => {
    return { available: true }
  }

  const executeExampleProductAction = async () => {}

  const setTyping = (isTyping: boolean) => {
    const conv = conversationRef.current
    if (!socket || !conv) return
    isSelfTypingRef.current = isTyping
    socket.emit(isTyping ? 'typing' : 'stop-typing', { roomId: conv.id })
  }

  return (
    <HelpChatSocketContext.Provider
      value={{
        socket,
        isConnected,
        connectionStatus,
        currentConversation,
        messages,
        isLoading,
        error,
        startConversation,
        sendMessage,
        uploadFile,
        transferToAdmin,
        cancelTransfer,
        startNewConversation,
        closeConversation,
        checkAdminAvailability,
        executeExampleProductAction,
        setTyping,
        isOtherTyping,
        isChatExpanded,
        setIsChatExpanded,
        isChatOpen,
        setIsChatOpen
      }}
    >
      {children}
    </HelpChatSocketContext.Provider>
  )
}

export function useHelpChatSocket() {
  const context = useContext(HelpChatSocketContext)
  if (context === undefined) {
    throw new Error('useHelpChatSocket must be used within HelpChatSocketProvider')
  }
  return context
}
